import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Heading,
    Text,
    Box,
    VStack,
    HStack,
    SimpleGrid,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Spinner,
    Center,
    useColorModeValue,
    Divider,
    Button,
    IconButton
} from '@chakra-ui/react';
import { ArrowUpIcon } from '@chakra-ui/icons';
import { ComparisonFilter, ComparisonType } from '../components/ComparisonFilter';
import { ComparisonCard } from '../components/ComparisonCard';
import { ComparisonApiService } from '../services/comparisonApiService';
import { ComparisonItem, ComparisonResponse } from '../types';

export const ComparisonPage: React.FC = () => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [comparisonData, setComparisonData] = useState<ComparisonResponse | null>(null);
    const [hasSearched, setHasSearched] = useState<boolean>(false);
    const [visibleCount, setVisibleCount] = useState<number>(10);
    const [showScrollTop, setShowScrollTop] = useState<boolean>(false);
    const observerRef = useRef<HTMLDivElement | null>(null);
    const [comparisonType, setComparisonType] = useState<ComparisonType>('JEJAK_SAKTI');
    const [appliedComparisonType, setAppliedComparisonType] = useState<ComparisonType>('JEJAK_SAKTI');


    // Scroll to top handler
    const handleScrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Listen to window scroll events to toggle scroll-to-top button visibility
    useEffect(() => {
        let scrollTimeout: NodeJS.Timeout;
        
        const handleScroll = () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                const scrollY = window.scrollY;
                setShowScrollTop(scrollY > 200);
            }, 50);
        };
        
        window.addEventListener('scroll', handleScroll, { passive: true });
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(scrollTimeout);
        };
    }, []);

    const handleSearch = async (estateId?: number, divisionId?: number, pemanenName?: string, date?: string, selectedComparisonType?: ComparisonType, discrepancies?: string[], differenceStatus?: string) => {
        setIsLoading(true);
        setError(null);
        setHasSearched(true);
        setVisibleCount(10);
        // Simpan comparison type HANYA saat Terapkan Filter ditekan
        if (selectedComparisonType) {
            setAppliedComparisonType(selectedComparisonType);
        }
        
        const searchDate = date || new Date().toISOString().split('T')[0];
        try {
            const res = await ComparisonApiService.fetchComparison(estateId, divisionId, pemanenName, searchDate, selectedComparisonType, discrepancies, differenceStatus);
            setComparisonData(res);
        } catch (err: any) {
            console.error("Error fetching comparison data:", err);
            setError(err.response?.data?.detail || "Gagal mengambil data perbandingan. Silakan periksa koneksi Anda.");
            setComparisonData(null);
        } finally {
            setIsLoading(false);
        }
    };

    // Run search automatically on initial load
    useEffect(() => {
        const today = new Date().toISOString().split('T')[0];
        handleSearch(undefined, undefined, undefined, undefined, undefined, undefined, today);
    }, []);

    // IntersectionObserver to load more items progressively and smoothly (5 items at a time)
    useEffect(() => {
        if (!comparisonData || visibleCount >= comparisonData.data.length) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    // Load 5 more cards to keep the rendering load light for the browser
                    setVisibleCount((prev) => Math.min(prev + 5, comparisonData.data.length));
                }
            },
            { threshold: 0.1, rootMargin: '200px' }
        );

        const currentRef = observerRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [comparisonData, visibleCount]);

    // Calculate aggregated statistics for the loaded items
    const getStatsSummary = () => {
        if (!comparisonData) return null;
        const list = comparisonData.data || (comparisonData as any).items || [];
        if (list.length === 0) return null;
        
        let totalJejakBunch = 0;
        let totalSaktiBunch = 0;
        let totalJejakRipe = 0;
        let totalSaktiRipe = 0;
        let totalJejakUnripe = 0;
        let totalSaktiUnripe = 0;
        let totalLooseDiff = 0;

        list.forEach((item: ComparisonItem) => {
            totalJejakBunch += item.mutu_buah.jejak_bunch_qty;
            totalSaktiBunch += item.sakti_bcc.sakti_bunch_qty;
            totalJejakRipe += item.mutu_buah.jejak_ripe_qty;
            totalSaktiRipe += item.sakti_bcc.sakti_ripe_qty;
            totalJejakUnripe += item.mutu_buah.jejak_unripe_qty;
            totalSaktiUnripe += item.sakti_bcc.sakti_unripe_qty;
            totalLooseDiff += item.discrepancy.loose_qty;
        });

        const bunchDiff = totalJejakBunch - totalSaktiBunch;
        const ripeDiff = totalJejakRipe - totalSaktiRipe;

        return {
            bunchDiff,
            ripeDiff,
            totalJejakBunch,
            totalSaktiBunch,
            totalJejakRipe,
            totalSaktiRipe,
            totalLooseDiff: Math.round(totalLooseDiff * 100) / 100
        };
    };

    const stats = getStatsSummary();
    const statBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const emptyBg = useColorModeValue('gray.50', 'gray.900');

    // Helper to get active items list safely
    const activeItems = comparisonData ? (comparisonData.data || (comparisonData as any).items || []) : [];
    // console.log(comparisonData)

    return (
        <Box minH="100vh" bg="gray.50" p={{ base: 4, md: 6 }}>
            <VStack spacing={6} align="stretch">
                {/* Heading */}
                <VStack align="start" spacing={1}>
                    <Heading size="lg" color="gray.700" mb={2}>
                        Perbandingan Inspeksi & Panen
                    </Heading>
                    <Text color="gray.600" fontSize="sm">
                        Perbandingan antara data hasil panen dengan hasil inspeksi
                    </Text>
                </VStack>

                {/* Filters */}
                <ComparisonFilter 
                    onSearch={handleSearch}
                    isLoading={isLoading} />

                {/* Loading state */}
                {isLoading && (
                    <Center h="300px">
                        <VStack spacing={4}>
                            <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="customGreen.500" size="xl" />
                            <Text color="gray.600">Sedang memproses data perbandingan...</Text>
                        </VStack>
                    </Center>
                )}

                {/* Error state */}
                {error && (
                    <Alert status="error" variant="subtle" borderRadius="lg" py={4}>
                        <AlertIcon />
                        <Box>
                            <AlertTitle fontSize="md">Gagal Memuat Perbandingan</AlertTitle>
                            <AlertDescription fontSize="sm">{error}</AlertDescription>
                        </Box>
                    </Alert>
                )}

                {/* Comparison Results */}
                {!isLoading && !error && hasSearched && (
                    <Box>
                        {activeItems.length > 0 ? (
                            <VStack align="stretch" spacing={4}>
                                <HStack justify="space-between" px={1}>
                                    <Text fontWeight="bold" fontSize="sm" color="gray.500">
                                        Hasil Pencarian ({activeItems.length} data pemanen)
                                    </Text>
                                    {/* <Text fontSize="xs" color="gray.400">
                                        Data dikelompokkan berurutan berdasarkan Blok, TPH, dan Pemanen
                                    </Text> */}
                                </HStack>
                                
                                {activeItems.slice(0, visibleCount).map((item, index) => (
                                    <ComparisonCard 
                                        key={`${item.block}-${item.tph}-${item.pemanen_nip}-${index}`} 
                                        item={item} 
                                        comparisonType={appliedComparisonType}
                                    />
                                ))}

                                {/* Progressive Scroll Sentinel */}
                                {visibleCount < activeItems.length && (
                                    <Box ref={observerRef} py={4} display="flex" justifyContent="center" alignItems="center">
                                        <Spinner size="md" color="customGreen.500" thickness="3px" speed="0.65s" />
                                        <Text ml={3} fontSize="sm" color="gray.500">Memuat lebih banyak...</Text>
                                    </Box>
                                )}
                            </VStack>
                        ) : (
                            <Center h="250px" bg={emptyBg} borderRadius="lg" border="1px dashed" borderColor={borderColor}>
                                <VStack spacing={2}>
                                    <Text fontWeight="semibold" color="gray.500">Tidak ada data ditemukan</Text>
                                    <Text fontSize="xs" color="gray.400">Tidak ada data perbandingan yang tersedia untuk filter yang dipilih.</Text>
                                </VStack>
                            </Center>
                        )}
                    </Box>
                )}

                {/* Initial State (Before searching) */}
                {!hasSearched && !isLoading && (
                    <Center h="300px" bg={emptyBg} borderRadius="lg" border="1px dashed" borderColor={borderColor}>
                        <VStack spacing={2} textAlign="center" px={6}>
                            <Heading size="xs" color="gray.500" textTransform="uppercase">Filter Perbandingan</Heading>
                            <Text fontSize="sm" color="gray.500" maxW="md">
                                Silakan tentukan **Estate**, **Divisi**, dan **Tanggal** di atas kemudian tekan tombol **Bandingkan Data** untuk menampilkan hasil.
                            </Text>
                        </VStack>
                    </Center>
                )}
            </VStack>

            {/* Scroll to Top Button */}
            {showScrollTop && (
                <IconButton
                    aria-label="Scroll ke atas"
                    icon={<ArrowUpIcon boxSize={6} />}
                    position="fixed"
                    bottom={8}
                    right={8}
                    zIndex={1005}
                    bg="customGreen.500"
                    color="white"
                    size="sm"
                    isRound
                    shadow="sm"
                    onClick={handleScrollToTop}
                    _hover={{ 
                        bg: "customGreen.600",
                        transform: "scale(1.05)"
                    }}
                    _active={{
                        bg: "customGreen.700",
                        transform: "scale(0.95)"
                    }}
                    border="2px solid white"
                    boxShadow="0 4px 12px rgba(0,0,0,0.15)"
                />
            )}
        </Box>
    );
};
